import { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { z } from 'zod'
import {
  Typography,
  Checkbox,
  TextField,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  Divider,
} from '@mui/material'
import { campaignRequirementCompositionOptions, campaignRequirementOptions } from '~/lib/types/Campaign'
import { CampaignRequirementOptions, FormatVersion, ReviewSection, ReviewTarget } from '~/generated/graphql'
import { colorPalette } from '~/constants/styles/colors'
import { CampaignInputComponent, CampaignInputComponentProps } from '~/features/campaign_input/CampaignInputComponent'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'
import { CustomAmountTextField } from '~/components/common/CustomAmountTextField'

export const grossSchemaFields = {
  sponsorServicePriceIntax: z.number(),
  basicGross: z
    .union([z.number(), z.literal('')])
    .transform((v) => (v === '' ? 0 : v))
    .refine((v) => v <= 100000, {
      message: '10万円以下で入力してください',
    })
    .refine((v) => v >= 0, {
      message: '0円以上で入力してください',
    }),
  campaignRequirementOptions: z.object({
    [CampaignRequirementOptions.DraftCheck]: z.boolean(),
    [CampaignRequirementOptions.SecondaryUse]: z.boolean(),
    [CampaignRequirementOptions.SpecifiedPrText]: z.boolean(),
    COMPOSITION_SPECIFICATION: z.boolean(),
  }),
  campaignRequirementOptionSpecifiedPrText: z.string().optional(),
  campaignRequirementCompositionOptions: z
    .enum([
      CampaignRequirementOptions.CompositionInfluencerAndService,
      CampaignRequirementOptions.CompositionProductOnly,
      CampaignRequirementOptions.CompositionUsageInContext,
    ])
    .optional(),
  gross: z.number().nullable(),
}

// フィールド合成で利用している箇所では sponsorServicePriceIntax でのバリデーションが効かないので
// 生成した zod object に対して validateGross を実行する
export const grossSchema = z.object(grossSchemaFields)
export function validateGross<T extends z.output<typeof grossSchema>>(v: T, ctx: z.RefinementCtx) {
  if (v.sponsorServicePriceIntax === 0 && v.basicGross < 1000) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '商材の単価が0円の場合、投稿報酬単価は1,000円以上で設定してください。',
      path: ['basicGross'],
    })
    return z.NEVER
  }
  // 指定PR文が選択されていて、文章が未入力のとき
  if (
    v.campaignRequirementOptions[CampaignRequirementOptions.SpecifiedPrText] &&
    !v.campaignRequirementOptionSpecifiedPrText?.trim()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '「インフルエンサーの投稿に含める文章」を入力してください',
      path: ['campaignRequirementOptionSpecifiedPrText'],
    })
  }

  // 構図指定が選択されていて、選択されていないとき
  if (v.campaignRequirementOptions.COMPOSITION_SPECIFICATION && !v.campaignRequirementCompositionOptions) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '「投稿される構図の指定」を選択してください',
      path: ['campaignRequirementCompositionOptions'],
    })
  }
  return v
}

type Props = CampaignInputComponentProps

export const Gross = ({ description }: Props) => {
  const { control, watch, setValue } = useFormContext<z.input<typeof grossSchema>>()
  const sponsorServicePriceIntax = watch('sponsorServicePriceIntax')
  const isZero = sponsorServicePriceIntax === 0
  const basicGrossRaw = watch('basicGross')
  const basicGross = basicGrossRaw === '' ? 0 : Number(basicGrossRaw)
  const selectedOptions = watch('campaignRequirementOptions')
  const optionsTotal = campaignRequirementOptions.reduce(
    (sum, option) => sum + (selectedOptions[option.id] ? option.price : 0),
    0,
  )
  const totalPrice = (Number(basicGross) || 0) + optionsTotal
  const [displayPrice, setDisplayPrice] = useState(totalPrice)

  useEffect(() => {
    if (isNaN(totalPrice)) return

    const duration = 200
    const start = displayPrice
    const end = totalPrice
    const startTime = performance.now()

    if (start === end) {
      setValue('gross', totalPrice)
      return
    }

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const newValue = Math.round(start + (end - start) * progress)
      setDisplayPrice(newValue)
      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        setValue('gross', totalPrice)
      }
    }
    const raf = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(raf)
  }, [totalPrice])

  return (
    <CampaignInputComponent
      title="投稿報酬単価（税抜）"
      description={description}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
      review={{
        section: ReviewSection.Campaign,
        targets: [ReviewTarget.SpecifiedPrText, ReviewTarget.LegalSpecifiedPrText],
      }}
    >
      <Grid container>
        <FormControl fullWidth sx={{ mb: 4 }}>
          <Controller
            name="basicGross"
            control={control}
            render={({ field: { ref, value, onChange, ...field }, fieldState: { error } }) => {
              return (
                <CustomAmountTextField
                  {...field}
                  inputRef={ref}
                  type="number"
                  placeholder="0"
                  value={value}
                  onChange={(e) => {
                    const val = e.target.value
                    // 数値または空文字だけを許容
                    if (val === '' || /^[0-9]+$/.test(val)) {
                      onChange(val === '' ? '' : Number(val))
                    }
                  }}
                  fullWidth
                  error={!!error}
                  helperText={<ValidationErrorMessage error={error?.message} />}
                  slotProps={{
                    input: {
                      inputMode: 'numeric',
                      endAdornment: <InputAdornment position="end">円</InputAdornment>,
                    },
                  }}
                  focused={value !== 0}
                />
              )
            }}
          />
          <Typography variant="caption" mt={1}>
            ※本項目は、無償提供する商材の本来の提供価格ではありません。無償提供物とは別に、金銭による報酬を支払う場合にのみ設定してください。
          </Typography>
          {isZero && (
            <Typography variant="caption" mt={1}>
              ※PR商材の本来の価格が0円のため、投稿報酬単価が1,000円以上の設定が必須です。
            </Typography>
          )}
        </FormControl>

        <FormControl fullWidth>
          <Typography variant="headingM" pb={1}>
            オプションの選択
          </Typography>
          <Grid container spacing={2}>
            {campaignRequirementOptions.map((option) => {
              const checkedOptions = selectedOptions[option.id]
              return (
                <Grid size={{ xs: 12, md: 6 }} key={option.id}>
                  <Paper
                    variant="outlined"
                    elevation={0}
                    sx={{
                      px: 3,
                      pt: 1.5,
                      pb: 2,
                      border: `2px solid ${checkedOptions ? colorPalette.info.outlineBorder : colorPalette.other.outlineBorder}`,
                      borderRadius: 2,
                      height: { md: '100%' },
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Controller
                          name={`campaignRequirementOptions.${option.id}`}
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={<Checkbox {...field} checked={field.value} />}
                              label={option.title}
                            />
                          )}
                        />
                        <Chip
                          label={`¥${option.price.toLocaleString()}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ p: 2, fontWeight: 'bold' }}
                        />
                      </Stack>
                      <Typography variant="body2">・{option.description}</Typography>
                      {option.id === CampaignRequirementOptions.SpecifiedPrText && (
                        <Controller
                          name="campaignRequirementOptionSpecifiedPrText"
                          control={control}
                          render={({ field: { ref, ...field }, fieldState }) => (
                            <Stack>
                              <FormLabel sx={{ mt: 1 }}>
                                インフルエンサーの投稿に含める文章を記載してください。
                              </FormLabel>
                              <TextField
                                {...field}
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={5}
                                placeholder="例: 商品の魅力や特徴について触れてください"
                                margin="normal"
                                disabled={!selectedOptions[CampaignRequirementOptions.SpecifiedPrText]}
                                error={!!fieldState.error}
                                helperText={<ValidationErrorMessage error={fieldState.error?.message} />}
                                inputRef={ref}
                              />
                            </Stack>
                          )}
                        />
                      )}
                      {option.id === 'COMPOSITION_SPECIFICATION' && (
                        <Controller
                          name="campaignRequirementCompositionOptions"
                          control={control}
                          render={({ field: { ref, ...field }, fieldState }) => (
                            <Stack>
                              <FormLabel sx={{ mt: 1 }}>投稿される構図の指定を選択してください。</FormLabel>
                              <FormControl component="fieldset" sx={{ mt: 1 }}>
                                <RadioGroup {...field}>
                                  {campaignRequirementCompositionOptions.map((compositionOption, index) => (
                                    <FormControlLabel
                                      key={compositionOption.id}
                                      value={compositionOption.id}
                                      control={
                                        <Radio
                                          size="small"
                                          disabled={!selectedOptions['COMPOSITION_SPECIFICATION']}
                                          inputRef={index === 0 ? ref : undefined}
                                        />
                                      }
                                      label={<Typography variant="body2">{compositionOption.title}</Typography>}
                                    />
                                  ))}
                                </RadioGroup>
                                {fieldState.error && <ValidationErrorMessage error={fieldState.error.message} />}
                              </FormControl>
                            </Stack>
                          )}
                        />
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              )
            })}
          </Grid>
        </FormControl>

        <Paper
          variant="outlined"
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            width: '100%',
            border: `2px solid ${totalPrice ? colorPalette.info.outlineBorder : colorPalette.other.outlineBorder}`,
            borderRadius: 2,
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">投稿報酬単価：</Typography>
              <Typography variant="body2">¥{basicGross.toLocaleString()}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">オプション単価：</Typography>
              <Typography variant="body2">¥{optionsTotal.toLocaleString()}</Typography>
            </Stack>
            <Divider sx={{ my: 3 }} />
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center">
              <Typography variant="body1" fontWeight="bold">
                インフルエンサー1人当たりの投稿報酬単価（税抜）
                <Typography component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                  ：
                </Typography>
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary" fontSize={20}>
                ¥{displayPrice.toLocaleString()}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
    </CampaignInputComponent>
  )
}
